import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus, Box, Lock, Unlock, Truck, X } from '@/lib/icons';

import {
  CATALOG_QUERY_POLICY,
  deviceCatalogKeys,
} from '@/features/device-models/keys';
import { getDeviceUnits } from '@/features/inventory/api';
import {
  TRANSFER_MAX_UNITS,
  type InventoryStatus,
  isEffectivelyLocked,
  isTransferable,
} from '@/features/inventory/device-unit';
import { ProviderBadge } from '@/features/inventory/components/ProviderBadge';
import { TransferDeviceDialog } from '@/features/inventory/components/TransferDeviceDialog';
import { VariantScopeChip } from '@/features/inventory/components/VariantScopeChip';
import {
  QuickLockUnlockDialog,
  type QuickLockUnlockTarget,
} from '@/features/inventory/components/QuickLockUnlockDialog';
import { useAppSelector } from '@/store/hooks';
import { canPerformDeviceAction } from '@/lib/auth/roles';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { ListFilterBar, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { useAuth } from '@/hooks/useAuth';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { formatDate } from '@/lib/dates';

const UNIT_STATUS_VALUES = [
  'in_stock',
  'reserved',
  'financed',
  'returned',
  'damaged',
  'written_off',
] as const;

const unitsSearchSchema = z.object({
  q: z.string().optional(),
  branchId: z.string().optional(),
  status: z.enum(UNIT_STATUS_VALUES).optional(),
  // Set when arriving from a device-model page: "the units of this variant".
  variantId: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/inventory/units/')({
  validateSearch: unitsSearchSchema,
  component: DeviceUnitsPage,
});

const inventoryStatusBadge = (status: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    procured: {
      cls: 'bg-slate-500/15 text-slate-600 border-slate-500/30 dark:text-slate-400',
      label: 'Procured',
    },
    in_stock: {
      cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
      label: 'In Stock',
    },
    reserved: {
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      label: 'Reserved',
    },
    financed: {
      cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
      label: 'Financed',
    },
    returned: {
      cls: 'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
      label: 'Returned',
    },
    damaged: {
      cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
      label: 'Damaged',
    },
    written_off: {
      cls: 'bg-gray-500/15 text-gray-500 border-gray-500/30 dark:text-gray-400',
      label: 'Written Off',
    },
  };
  const s = map[status] ?? { cls: '', label: status };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

const conditionBadge = (condition: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    new: {
      cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
      label: 'New',
    },
    refurbished: {
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      label: 'Refurbished',
    },
    damaged: {
      cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
      label: 'Damaged',
    },
  };
  const s = map[condition] ?? { cls: '', label: condition };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

const PAGE_SIZE = 20;

function DeviceUnitsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';
  // See the note in the inventory dashboard: enrolment is admin-only, so the
  // entry points come down rather than bouncing a branch manager who taps one.
  const canEnroll = useAuth().hasPermission('device_units', 'create');
  // Lock / Unlock are gated per-row by the matching device_units permission
  // (lock vs unlock) — checked at the button below since the action depends
  // on the row's current lock state. Admins bypass; agents without the grant
  // see no toggle.
  const user = useAppSelector((state) => state.auth.user);
  const search = Route.useSearch();
  const [quickLockTarget, setQuickLockTarget] =
    useState<QuickLockUnlockTarget | null>(null);
  // Bulk transfer. Selection is keyed by unit id and deliberately survives
  // paging and filtering — an operator packing a shipment picks handsets from
  // several pages, and losing the basket on a page turn is what made the
  // single-unit flow the only usable one.
  const canTransfer = canPerformDeviceAction(user, 'transfer');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [transferOpen, setTransferOpen] = useState(false);

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount: sharedActiveCount,
  } = useListFilters({ search, to: '/inventory/units' });
  // The shared hook counts only the facets every list has; the variant scope
  // is this list's own, and must count so Reset appears and the empty state
  // says "filtered" rather than "no units".
  const activeCount = sharedActiveCount + (search.variantId ? 1 : 0);

  const page = search.page ?? 1;

  const unitFilters = {
    search: debouncedSearch,
    status: search.status as InventoryStatus | undefined,
    branchId: search.branchId,
    variantId: search.variantId,
  };
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: deviceCatalogKeys.unitList(unitFilters),
    queryFn: () => getDeviceUnits(unitFilters),
    placeholderData: (previous) => previous,
    ...CATALOG_QUERY_POLICY,
  });

  // Memoised so the selection lookup below keeps a stable dependency — a
  // fresh `[]` on every render would recompute it on each keystroke.
  const allUnits = useMemo(() => data?.value ?? [], [data]);
  const totalPages = Math.max(1, Math.ceil(allUnits.length / PAGE_SIZE));
  const units = allUnits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const startItem = allUnits.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, allUnits.length);

  // Resolved against the full fetched list, not the visible page, so a
  // selection made on page 1 still dispatches after paging to page 3.
  const selectedUnits = useMemo(
    () => allUnits.filter((u) => selectedIds.has(u.id)),
    [allUnits, selectedIds]
  );
  const selectablePage = units.filter(isTransferable);
  const pageAllSelected =
    selectablePage.length > 0 &&
    selectablePage.every((u) => selectedIds.has(u.id));

  const toggleUnit = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const togglePage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const u of selectablePage) {
        if (pageAllSelected) next.delete(u.id);
        else next.add(u.id);
      }
      return next;
    });

  const chips: ReadonlyArray<
    FilterChip<'all' | (typeof UNIT_STATUS_VALUES)[number]>
  > = [
    { value: 'all', label: 'All' },
    ...UNIT_STATUS_VALUES.map((value) => ({
      value,
      label: t(`inventory.status.${value}`),
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('inventory.units.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('inventory.units.description')}
          </p>
        </div>
        {canEnroll && (
          <Button
            className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
            onClick={() => navigate({ to: '/inventory/units/enroll' })}
          >
            <Plus className="w-4 h-4 mr-2" /> {t('inventory.units.enrollUnit')}
          </Button>
        )}
      </div>

      <ListFilterBar
        chips={chips}
        chipValue={search.status ?? 'all'}
        onChipChange={(value) =>
          setFilters({ status: value === 'all' ? undefined : value })
        }
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.units')}
        isSearchPending={isSearchPending}
        branchId={search.branchId}
        onBranchChange={(branchId) => setFilters({ branchId })}
        activeCount={activeCount}
        onReset={reset}
        isPending={isFetching}
      >
        {search.variantId && (
          <VariantScopeChip
            variantId={search.variantId}
            onClear={() => setFilters({ variantId: undefined })}
          />
        )}
      </ListFilterBar>

      {/* Selection bar — appears only once something is picked, so the list
          reads identically to before for operators who never bulk-transfer. */}
      {canTransfer && selectedIds.size > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 shadow-sm backdrop-blur">
          <span className="text-sm font-medium">
            {t('inventory.units.selectedCount', { count: selectedIds.size })}
          </span>
          {selectedIds.size > TRANSFER_MAX_UNITS && (
            <span className="text-xs text-destructive">
              {t('inventory.transfer.overCap', { max: TRANSFER_MAX_UNITS })}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              {t('inventory.units.clearSelection')}
            </Button>
            <Button
              size="sm"
              onClick={() => setTransferOpen(true)}
              disabled={selectedIds.size > TRANSFER_MAX_UNITS}
            >
              <Truck className="w-3.5 h-3.5 mr-1.5" />
              {t('inventory.units.transferSelected')}
            </Button>
          </div>
        </div>
      )}

      <Card className="shadow-md border-0">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title="Failed to load device units"
              />
            </div>
          ) : allUnits.length === 0 ? (
            <NoFilterResults
              isFiltered={activeCount > 0}
              emptyTitle={t('inventory.units.empty.title')}
              emptyDescription={t('inventory.units.empty.description')}
              icon={Box}
              onReset={reset}
              action={
                canEnroll ? (
                  <Button
                    onClick={() => navigate({ to: '/inventory/units/enroll' })}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('inventory.units.enrollUnit')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    {canTransfer && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={pageAllSelected}
                          onCheckedChange={togglePage}
                          disabled={selectablePage.length === 0}
                          aria-label={t('inventory.units.selectPage')}
                        />
                      </TableHead>
                    )}
                    <TableHead>{t('inventory.units.columns.imei')}</TableHead>
                    <TableHead>{t('inventory.units.columns.model')}</TableHead>
                    <TableHead>
                      {t('inventory.units.columns.variant')}
                    </TableHead>
                    <TableHead>{t('inventory.units.columns.status')}</TableHead>
                    <TableHead>
                      {t('inventory.units.columns.condition')}
                    </TableHead>
                    <TableHead>
                      {t('inventory.units.columns.lockStatus')}
                    </TableHead>
                    <TableHead>
                      {t('inventory.units.columns.provider')}
                    </TableHead>
                    <TableHead>{t('inventory.units.columns.price')}</TableHead>
                    <TableHead>
                      {t('inventory.units.columns.enrolled')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.units.columns.quickAction')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {units.map((u) => (
                    <TableRow
                      key={u.id}
                      data-state={
                        selectedIds.has(u.id) ? 'selected' : undefined
                      }
                      className="hover:bg-muted/30 transition-colors cursor-pointer data-[state=selected]:bg-primary/5"
                      onClick={() =>
                        navigate({
                          to: '/inventory/units/$unitId',
                          params: { unitId: u.id },
                        })
                      }
                    >
                      {canTransfer && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(u.id)}
                            onCheckedChange={() => toggleUnit(u.id)}
                            disabled={!isTransferable(u)}
                            aria-label={t('inventory.units.selectUnit', {
                              imei: u.imei,
                            })}
                            title={
                              isTransferable(u)
                                ? undefined
                                : t('inventory.units.notTransferable')
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {u.imei}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {u.brand ?? ''} {u.modelName ?? ''}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {[u.storageCapacity, u.color]
                          .filter(Boolean)
                          .join(' / ') || '-'}
                      </TableCell>
                      <TableCell>
                        {inventoryStatusBadge(u.inventoryStatus)}
                      </TableCell>
                      <TableCell>{conditionBadge(u.condition)}</TableCell>
                      <TableCell>
                        {isEffectivelyLocked(u) ? (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                            <Lock className="w-3.5 h-3.5" /> Locked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <Unlock className="w-3.5 h-3.5" /> Unlocked
                          </span>
                        )}
                        {u.deviceLockStatus != null &&
                          u.deviceLockStatus !== u.lockStatus && (
                            <span
                              className="ml-1 text-[10px] text-amber-600 dark:text-amber-400"
                              title={t(
                                'inventory.quickLock.driftIndicatorTooltip'
                              )}
                            >
                              {t('inventory.quickLock.driftIndicator')}
                            </span>
                          )}
                      </TableCell>
                      <TableCell>
                        <ProviderBadge providerName={u.providerName} />
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(u.effectiveRetailPrice, currency)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(u.enrolledAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        {canPerformDeviceAction(
                          user,
                          isEffectivelyLocked(u) ? 'unlock' : 'lock'
                        ) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className={
                              isEffectivelyLocked(u)
                                ? 'h-8 border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300'
                                : 'h-8 border-red-500/40 text-red-700 hover:bg-red-500/10 dark:text-red-300'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickLockTarget({
                                unitId: u.id,
                                imei: u.imei,
                                brand: u.brand,
                                modelName: u.modelName,
                                action: isEffectivelyLocked(u)
                                  ? 'unlock'
                                  : 'lock',
                                providerName: u.providerName,
                                currentLockStatus: u.lockStatus,
                              });
                            }}
                          >
                            {isEffectivelyLocked(u) ? (
                              <>
                                <Unlock className="w-3.5 h-3.5 mr-1.5" />
                                {t('inventory.quickLock.unlock')}
                              </>
                            ) : (
                              <>
                                <Lock className="w-3.5 h-3.5 mr-1.5" />
                                {t('inventory.quickLock.lock')}
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {allUnits.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {startItem}-{endItem} of {allUnits.length}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFilters({ page: Math.max(1, page - 1) })}
                  className={
                    page <= 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    isActive={page === i + 1}
                    onClick={() => setFilters({ page: i + 1 })}
                    className="cursor-pointer"
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setFilters({ page: Math.min(totalPages, page + 1) })
                  }
                  className={
                    page >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <QuickLockUnlockDialog
        target={quickLockTarget}
        onClose={() => setQuickLockTarget(null)}
      />

      <TransferDeviceDialog
        units={selectedUnits}
        open={transferOpen}
        onOpenChange={setTransferOpen}
        onTransferred={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
